/**
 * What the exceptions board's detail panel says about the selected row.
 *
 * The panel has four derived parts — a coloured callout naming why this row is on the board, a list of
 * what an operator can do next, a timeline, and the published field table that travels on the row
 * itself. Three of them are pure functions of the row and the clock, so they live here rather than
 * inside the rendering: the rules they encode are the screen's argument, and an argument that can be
 * read on its own can be tested on its own.
 *
 * Two rules govern every string below.
 *
 * **Published fields only.** Where the design shows something no contract publishes, it is absent
 * rather than approximated. Three things are missing and each is named in the report rather than
 * invented: DEL-08 publishes `deliveryOrder` and no address, so there is no place name; DSP-05
 * publishes `attempt` and not `maximumAttempts`, so "3 of 3" is "3"; and neither 409 response
 * publishes who made the conflicting change.
 *
 * **Each action names the operation it calls, and says what it needs.** The design's notes were
 * written when DEL-08's `TransitionCommand`, DEL-08's `OpenCommand` and LOG-12's `ReturnRequest` were
 * unpublished. `docs/design/DESIGN_RECONCILIATION.md` records all three as closed — G-01, G-02, G-03
 * — and the operation catalogue confirms it, so the notes here name the fields those payloads now
 * publish. A note telling an operator a payload does not exist when it does is worse than no note.
 */

import { formatAge, formatClock, type TriageRow } from "./triage.ts";

/**
 * Which hue a callout or a timeline dot takes.
 *
 * The tones are the semantic ones from the token palette, not colours: the panel asks for "danger" and
 * the theme decides what danger looks like, which is the only way one component works in both themes.
 */
export type DetailTone = "danger" | "warning" | "attention" | "info" | "neutral";

/** The coloured box under the headline, saying why this row is on the board. */
export interface DetailCallout {
  readonly glyph: string;
  readonly tone: DetailTone;
  readonly title: string;
  readonly body: string;
}

/** One entry in "What happens next". */
export interface DetailAction {
  readonly glyph: string;
  readonly label: string;
  /** The service and operation this calls, drawn at the end of the button in Geist Mono. */
  readonly operation: string;
  /** What it needs, or what is missing. */
  readonly note: string;
  /** The first action of a set is the one an operator most often wants. */
  readonly primary?: boolean;
  /** True when nothing published can do this yet, which the note then explains. */
  readonly missing?: boolean;
}

/** One entry in the timeline. */
export interface DetailEvent {
  readonly text: string;
  /** When, and which published field it was read from. */
  readonly at: string;
  readonly tone: DetailTone;
}

/**
 * The sentence under the headline.
 *
 * Delivery says it in words because its `kind` and `reasonCode` are two enums that mean one thing
 * together; the other two sources already have a sentence on the row.
 */
export function detailSentence(row: TriageRow): string {
  if (row.source !== "delivery") return row.detail;
  const kind = row.fields.find((field) => field.name === "kind")?.value;
  const reason = row.fields.find((field) => field.name === "reasonCode")?.value;
  const what = kind === "pickup_failed" ? "Pickup" : "Delivery";
  return `${what} failed with reasonCode ${reason ?? "unknown"}.`;
}

/** How the panel names the row and its version: "DSP-05 dispatch operation · v3". */
export function detailSourceLine(row: TriageRow): string {
  // A read model has no version and cannot be commanded with one. Saying so is the point: an operator
  // who sees "v3" on one row and nothing on another learns which rows have optimistic concurrency.
  return row.version === 0
    ? `${row.serviceName} · read model, unversioned`
    : `${row.serviceName} · v${row.version}`;
}

/**
 * Why this row is here, and what happens if nobody touches it.
 *
 * One callout per source, because the three sources mean different things by urgent: a dispatch search
 * is racing a deadline, a delivery exception is waiting for a decision, a logistics exception has a
 * severity somebody assigned.
 */
export function detailCallout(row: TriageRow, now: number): DetailCallout {
  if (row.source === "dispatch") {
    if (row.status === "failed") {
      return {
        glyph: "error",
        tone: "danger",
        title: "The search ran out",
        // "3 of 3" in the design. DSP-05 publishes `attempt` and no maximum — that comes from DSP-04,
        // which this board does not read — so the count is the one number that is true here.
        body:
          `${row.attempt ?? 0} attempts used` +
          `${row.offersCancelled === true ? ", offers cancelled" : ""}. ` +
          `Assign manually or cancel so the rider isn't left waiting.`,
      };
    }
    const remaining = row.deadlineAt === undefined ? 0 : Date.parse(row.deadlineAt) - now;
    const breaching = remaining < 2 * 60 * 1000;
    return {
      glyph: breaching ? "timer" : "schedule",
      tone: breaching ? "danger" : "attention",
      title: `Deadline in ${formatClock(remaining)}`,
      body: `Attempt ${row.attempt ?? 0}. If it passes, the saga fails and this row turns red.`,
    };
  }

  if (row.source === "delivery") {
    if (row.deadlineAt !== undefined) {
      return {
        glyph: "event_repeat",
        tone: "warning",
        title: `Retry in ${formatClock(Date.parse(row.deadlineAt) - now)}`,
        body: "A courier will attempt again. Nothing to do unless the window changes.",
      };
    }
    return {
      glyph: "back_hand",
      tone: "attention",
      title: `Open for ${formatAge(now - Date.parse(row.occurredAt))}`,
      body: "Waiting for a decision: retry, return or resolve.",
    };
  }

  const critical = row.severity === "critical";
  const severity = row.severity ?? "unknown";
  return {
    glyph: critical ? "error" : "info",
    tone: critical ? "danger" : "info",
    title: `${severity.slice(0, 1).toUpperCase()}${severity.slice(1)} severity`,
    body: row.status === "investigating" ? "Being investigated." : "Nobody has picked this up yet.",
  };
}

/**
 * What an operator can do next, each labelled with the service and operation it calls.
 *
 * The notes are the useful half. Two of these actions have no service behind them at all — there is no
 * contact service and "let it run" is not a command — and an operator finding that out by clicking is
 * an operator who has lost trust in every other button on the panel.
 */
export function detailActions(row: TriageRow): readonly DetailAction[] {
  if (row.source === "delivery") {
    return row.status === "open"
      ? [
          {
            glyph: "event_repeat",
            label: "Schedule a retry",
            operation: "DEL-08 · commands",
            note: "Takes type, expectedVersion, retryAt and occurredAt. Retry window uses DEL-09 schedules.",
            primary: true,
          },
          {
            glyph: "keyboard_return",
            label: "Return to origin",
            operation: "DEL-08 · commands",
            note: "Takes type, expectedVersion and occurredAt. Creates a return with LOG-12, which takes originalShipmentId, reasonCode, itemCount and weightGrams.",
          },
          {
            glyph: "call",
            label: "Contact recipient",
            operation: "—",
            note: "No contact service is published. Proposed.",
            missing: true,
          },
          {
            glyph: "task_alt",
            label: "Resolve",
            operation: "DEL-08 · commands",
            note: "Takes type, expectedVersion, resolutionCode and occurredAt.",
          },
        ]
      : [
          {
            glyph: "task_alt",
            label: "Resolve",
            operation: "DEL-08 · commands",
            note: "Takes type, expectedVersion, resolutionCode and occurredAt.",
            primary: true,
          },
          {
            glyph: "cancel",
            label: "Cancel exception",
            operation: "DEL-08 · commands",
            note: "Takes type, expectedVersion and occurredAt.",
          },
        ];
  }

  if (row.source === "logistics") {
    return [
      {
        glyph: row.status === "open" ? "manage_search" : "task_alt",
        label: row.status === "open" ? "Start investigating" : "Resolve with a code",
        operation: "LOG-11 · transition",
        note: "Takes status, expectedVersion, occurredAt, resolutionCode, resolutionNotes. Fully published.",
        primary: true,
      },
      {
        glyph: "keyboard_return",
        label: "Start a return",
        operation: "LOG-12 · returns",
        note: "Takes originalShipmentId, reasonCode, itemCount and weightGrams.",
      },
    ];
  }

  return row.status === "failed"
    ? [
        {
          glyph: "assignment_ind",
          label: "Assign a driver manually",
          operation: "DSP-03 · ride-assignments",
          note: "Needs ride-assignment:commit and a reason. Audited.",
          primary: true,
        },
        {
          glyph: "replay",
          label: "Start a new search",
          operation: "DSP-04 · events",
          note: "Needs dispatch:orchestrate.",
        },
        {
          glyph: "cancel",
          label: "Cancel the request",
          operation: "MOB-03",
          note: "Rider is told immediately; no charge.",
        },
      ]
    : [
        {
          glyph: "assignment_ind",
          label: "Assign a driver manually",
          operation: "DSP-03 · ride-assignments",
          note: "Overrides the running search. Needs a reason.",
          primary: true,
        },
        {
          glyph: "more_time",
          label: "Let it run",
          operation: "—",
          note: "No action. The row stays pinned until the deadline.",
        },
      ];
}

/**
 * The timeline, built from the timestamps each source publishes and nothing else.
 *
 * Every entry names the field it was read from, so what an operator is looking at is checkable. There
 * is no audit history here: AUD-01 exists and this screen is not wired to it, which the panel says
 * under the list rather than implying completeness by staying quiet.
 */
export function detailTimeline(row: TriageRow, now: number): readonly DetailEvent[] {
  const opened: DetailEvent = {
    text: row.source === "dispatch" ? "Demand created, search started" : "Exception opened",
    at: `${formatAge(now - Date.parse(row.occurredAt))} ago · ${row.occurredAtField}`,
    tone: "neutral",
  };

  const progress: DetailEvent =
    row.source === "dispatch"
      ? {
          text: `Attempt ${row.attempt ?? 0} ${row.status === "failed" ? "failed" : "running"}`,
          at: "observedAt · live read model",
          tone: row.status === "failed" ? "danger" : "info",
        }
      : { text: `Status: ${row.status}`, at: "updatedAt", tone: "attention" };

  if (row.source !== "delivery" || row.deadlineAt === undefined) return [opened, progress];
  return [
    opened,
    progress,
    {
      text: "Retry attempt",
      at: `in ${formatClock(Date.parse(row.deadlineAt) - now)} · retryAt`,
      tone: "warning",
    },
  ];
}
